// Vendor Billing — the Vendor portal "Invoices" tab: list +
// detail (the detail open stamps ViewedAt server-side) + the Stripe pay
// flow (ACH Direct Debit + card via InvoicePayPanel). Three clicks from
// the dashboard: row → Pay now → confirm in the Payment Element.

import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ReceiptText } from "lucide-react";
import { Button, EmptyState, StatusBadge } from "@/design-system/components/ui";
import type { StatusTone } from "@/design-system/components/ui";
import { DataTable, type Column } from "@/components/layout/DataTable";
// Dates go through the PURE formatters with an EXPLICIT null zone, never the
// staff date-formatter hook in `@/utils/datetime` — that hook reads the staff
// AuthContext, which this tree never mounts. See portal/isolation.test.ts
// (FORBIDDEN_PREFIXES) and portal/portalInstantsRenderInOneZone.test.tsx.
import { formatCalendarDate, formatDateInTz } from "@/design-system/lib/format";
import { formatCurrency } from "@/design-system/lib/format";
import {
  portalVendorApi,
  type PortalVendorInvoiceDto,
  type PortalVendorInvoiceStatus,
} from "../api/vendor";
import { InvoicePayPanel, type PaymentSettledKind } from "./InvoicePayPanel";
import { VendorInvoiceSummary } from "./VendorInvoiceSummary";
import { StatementDownloadBar } from "../components/StatementDownloadBar";

/** Statuses the pay flow can start from (mirrors the server rule). */
const PAYABLE_STATUSES: readonly PortalVendorInvoiceStatus[] = [
  "issued",
  "viewed",
  "overdue",
];

const ACH_PROCESSING_MESSAGE =
  "Payment processing — bank transfers take a few days.";

const STATUS_LABEL: Record<PortalVendorInvoiceStatus, string> = {
  issued: "Issued",
  viewed: "Viewed",
  processing: "Processing",
  paid: "Paid",
  overdue: "Overdue",
  void: "Void",
};

// Same condition, same color as the staff app: overdue = error,
// processing = info, paid = success, issued/viewed = neutral.
function statusTone(status: PortalVendorInvoiceStatus): StatusTone {
  switch (status) {
    case "paid":
      return "ok";
    case "overdue":
      return "danger";
    case "processing":
      return "info";
    case "void":
      return "muted";
    default:
      return "neutral";
  }
}

function money(amount: number): string {
  return formatCurrency(amount, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

export function InvoicesTab() {
  const [rows, setRows] = useState<PortalVendorInvoiceDto[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [params, setParams] = useSearchParams();
  const openInvoiceId = params.get("invoice");

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const data = await portalVendorApi.listInvoices();
        if (!cancelled) setRows(data);
      } catch (err) {
        if (!cancelled)
          setError(err instanceof Error ? err.message : "Couldn't load invoices.");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const setOpenInvoiceId = (id: string | null) => {
    const updated = new URLSearchParams(params);
    if (id) updated.set("invoice", id);
    else updated.delete("invoice");
    setParams(updated, { replace: true });
  };

  if (error) {
    return (
      <p className="text-small text-status-error" role="alert">
        {error}
      </p>
    );
  }
  if (!rows) {
    return (
      <p className="text-small text-gray-mid" data-testid="invoices-loading">
        Loading your invoices…
      </p>
    );
  }

  if (openInvoiceId) {
    return (
      <InvoiceDetail
        invoiceId={openInvoiceId}
        onBack={() => setOpenInvoiceId(null)}
        // Stale-after-save round 2 — the list and the detail share this
        // mounted component (?invoice= param), so a settled payment must
        // patch the list row or "Back to invoices" shows the old status.
        onUpdated={(updated) =>
          setRows((prev) =>
            prev ? prev.map((r) => (r.id === updated.id ? updated : r)) : prev,
          )
        }
      />
    );
  }

  if (rows.length === 0) {
    return (
      <EmptyState
        icon={ReceiptText}
        title="No invoices yet"
        description="Invoices Phoenix Capital issues to you will appear here, ready to pay online by bank transfer or card."
      />
    );
  }

  return (
    <section aria-label="Invoices" className="flex flex-col gap-space-4">
      {/* External party portals PR-D — at-a-glance financial summary + per-
          customer aggregate over the same invoice list (no new endpoint). */}
      <VendorInvoiceSummary invoices={rows} />
      <StatementDownloadBar
        onDownload={(p) => portalVendorApi.statement(p)}
        filename={(p) => `invoice-statement-${p}.pdf`}
      />
      <DataTable<PortalVendorInvoiceDto>
        rows={rows}
        rowKey={(i) => i.id}
        sortable
        defaultSort={{ key: "issued", dir: "desc" }}
        pagination={{ pageSize: 15 }}
        emptyMessage="No invoices match."
        onRowClick={(i) => setOpenInvoiceId(i.id)}
        columns={invoiceColumns()}
      />
    </section>
  );
}

function invoiceColumns(): Column<PortalVendorInvoiceDto>[] {
  return [
    {
      key: "invoice",
      header: "Invoice",
      sortable: true,
      sortAccessor: (i) => i.invoiceNumber,
      render: (i) => (
        <span
          className="text-small font-medium text-onyx"
          data-testid={`invoice-row-${i.id}`}
        >
          {i.invoiceNumber}
        </span>
      ),
    },
    {
      key: "client",
      header: "Client",
      mobileHidden: true,
      sortable: true,
      sortAccessor: (i) => i.loanDisplayName,
      render: (i) => <span className="text-small">{i.loanDisplayName}</span>,
    },
    {
      key: "amount",
      header: "Amount",
      sortable: true,
      sortAccessor: (i) => i.amount,
      render: (i) => (
        <span className="text-small tabular-nums block text-right">
          {money(i.amount)}
        </span>
      ),
    },
    {
      key: "due",
      header: "Due",
      sortable: true,
      sortAccessor: (i) => (i.dueDate ? Date.parse(i.dueDate) : 0),
      render: (i) => <CalendarDateCell at={i.dueDate} />,
    },
    {
      key: "issued",
      header: "Issued",
      mobileHidden: true,
      sortable: true,
      sortAccessor: (i) => (i.issuedAt ? Date.parse(i.issuedAt) : 0),
      render: (i) => <DateCell at={i.issuedAt} />,
    },
    {
      key: "status",
      header: "Status",
      sortable: true,
      sortAccessor: (i) => i.status,
      render: (i) => (
        <StatusBadge tone={statusTone(i.status)} struck={i.status === "void"}>
          {STATUS_LABEL[i.status]}
        </StatusBadge>
      ),
    },
  ];
}

function DateCell({ at }: { at: string | null }) {
  return (
    <span className="text-caption text-gray-mid">{at ? formatDateInTz(at, null) : "—"}</span>
  );
}

/**
 * The wall-calendar twin of `DateCell`, rendered in UTC.
 *
 * Two cells rather than a prop because the distinction is about the VALUE, not
 * the styling: `issuedAt` is a clock reading and must move with the viewer's
 * zone, while `dueDate` is a day somebody picked. `VendorInvoice.DueDate`
 * reaches us at UTC midnight — `IssueInvoiceModal` posted
 * `new Date(`${dueDate}T00:00:00Z`)` from PR #617 until #1032 — so through a
 * tz-aware formatter it renders as the PREVIOUS DAY, and this is the invoice's
 * own vendor reading their own deadline.
 */
function CalendarDateCell({ at }: { at: string | null }) {
  return (
    <span className="text-caption text-gray-mid">
      {at ? formatCalendarDate(at) : "—"}
    </span>
  );
}

function InvoiceDetail({
  invoiceId,
  onBack,
  onUpdated,
}: {
  invoiceId: string;
  onBack: () => void;
  /** Mirrors the settled invoice into the parent's list rows. */
  onUpdated: (updated: PortalVendorInvoiceDto) => void;
}) {
  const [invoice, setInvoice] = useState<PortalVendorInvoiceDto | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [paying, setPaying] = useState(false);
  const [justSettled, setJustSettled] = useState<PaymentSettledKind | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        // This read stamps the invoice as viewed on its first open.
        const data = await portalVendorApi.getInvoice(invoiceId);
        if (!cancelled) setInvoice(data);
      } catch (err) {
        if (!cancelled)
          setError(err instanceof Error ? err.message : "Couldn't load the invoice.");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [invoiceId]);

  if (error) {
    return (
      <div className="flex flex-col items-start gap-space-3">
        <p className="text-small text-status-error m-0" role="alert">{error}</p>
        <Button variant="secondary" size="sm" onClick={onBack}>
          Back to invoices
        </Button>
      </div>
    );
  }
  if (!invoice) {
    return (
      <p className="text-small text-gray-mid" data-testid="invoice-detail-loading">
        Loading invoice…
      </p>
    );
  }

  const payable = PAYABLE_STATUSES.includes(invoice.status);
  const payments = invoice.payments ?? [];

  const onSettled = (updated: PortalVendorInvoiceDto, kind: PaymentSettledKind) => {
    setInvoice(updated);
    onUpdated(updated);
    setPaying(false);
    setJustSettled(kind);
  };

  return (
    <section
      aria-label={`Invoice ${invoice.invoiceNumber}`}
      className="flex flex-col gap-space-4"
      data-testid="invoice-detail"
    >
      <button
        type="button"
        className="self-start text-small text-gold-shadow hover:underline"
        onClick={onBack}
        data-testid="invoice-detail-back"
      >
        ← Back to invoices
      </button>

      <div className="rounded-lg border border-border-light bg-white-pure p-space-4 flex flex-col gap-space-3">
        <div className="flex items-center justify-between gap-space-2 flex-wrap">
          <h2 className="font-display font-bold text-h2 m-0">
            {invoice.invoiceNumber}
          </h2>
          <StatusBadge tone={statusTone(invoice.status)} struck={invoice.status === "void"}>
            {STATUS_LABEL[invoice.status]}
          </StatusBadge>
        </div>

        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-y-space-1 text-small m-0">
          <dt className="text-caption text-gray-mid uppercase tracking-label">Client</dt>
          <dd className="m-0">{invoice.loanDisplayName}</dd>
          <dt className="text-caption text-gray-mid uppercase tracking-label">Amount</dt>
          <dd className="m-0 tabular-nums">{money(invoice.amount)}</dd>
          <dt className="text-caption text-gray-mid uppercase tracking-label">Issued</dt>
          <dd className="m-0">{invoice.issuedAt ? formatDateInTz(invoice.issuedAt, null) : "—"}</dd>
          <dt className="text-caption text-gray-mid uppercase tracking-label">Due</dt>
          <dd className="m-0">{invoice.dueDate ? formatCalendarDate(invoice.dueDate) : "—"}</dd>
          {invoice.paidAt && (
            <>
              <dt className="text-caption text-gray-mid uppercase tracking-label">Paid</dt>
              <dd className="m-0">{formatDateInTz(invoice.paidAt, null)}</dd>
            </>
          )}
          {invoice.description && (
            <>
              <dt className="text-caption text-gray-mid uppercase tracking-label">Details</dt>
              <dd className="m-0 whitespace-pre-wrap">{invoice.description}</dd>
            </>
          )}
        </dl>

        {justSettled === "paid" && (
          <p
            className="text-small text-status-success m-0"
            role="status"
            data-testid="invoice-paid-confirmation"
          >
            Payment received — thank you. Your receipt is on its way by email.
          </p>
        )}
        {invoice.status === "processing" && (
          <p
            className="text-small text-status-info m-0"
            role="status"
            data-testid="invoice-processing-note"
          >
            {ACH_PROCESSING_MESSAGE} We&apos;ll email you when it completes.
          </p>
        )}

        {payable && !paying && (
          <div className="flex flex-col items-start gap-space-1">
            <Button
              variant="primary"
              onClick={() => setPaying(true)}
              data-testid="invoice-pay-button"
            >
              Pay {money(invoice.amount)}
            </Button>
            <span className="text-caption text-gray-mid">
              Pay securely online by bank transfer (ACH) or card.
            </span>
          </div>
        )}
        {payable && paying && (
          <InvoicePayPanel
            invoiceId={invoice.id}
            amountLabel={money(invoice.amount)}
            createIntent={portalVendorApi.createPaymentIntent}
            syncPayment={portalVendorApi.syncPayment}
            onSettled={onSettled}
            onCancel={() => setPaying(false)}
          />
        )}
      </div>

      {payments.length > 0 && (
        <div className="rounded-lg border border-border-light bg-white-pure p-space-4 flex flex-col gap-space-2">
          <h3 className="font-display font-semibold text-h4 m-0">Payment history</h3>
          <ul
            className="m-0 p-0 list-none flex flex-col gap-space-1"
            data-testid="invoice-payment-history"
          >
            {payments.map((p, index) => (
              <li
                key={`${p.receivedAt}-${index}`}
                className="flex flex-col gap-space-1 text-small border-b border-border-light pb-space-1"
              >
                <span className="flex items-baseline justify-between gap-space-2">
                  <span className={p.reversedAt ? "line-through text-gray-mid" : undefined}>
                    {formatDateInTz(p.receivedAt, null)} · {PAYMENT_METHOD_LABEL[p.method] ?? p.method}
                  </span>
                  <span
                    className={`text-right tabular-nums ${
                      p.reversedAt ? "line-through text-gray-mid" : ""
                    }`}
                  >
                    {money(p.amount)}
                  </span>
                </span>
                {/* Without this, an invoice back at "issued" still showed the
                    payment as received — the vendor would owe a balance while
                    their own history said they had paid it. */}
                {p.reversedAt && (
                  <span className="text-caption text-status-error">
                    Reversed by Phoenix Capital on {formatDateInTz(p.reversedAt, null)}
                  </span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}

const PAYMENT_METHOD_LABEL: Record<string, string> = {
  manual: "Recorded by Phoenix Capital",
  card: "Card",
  us_bank_account: "Bank transfer (ACH)",
};
